import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import dayjs from "dayjs";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { DatePickerV2 } from "@/components/fields/date-picker";
import { Separator } from "@/components/ui/separator";

import { cn } from "@/lib/utils";

interface DateRange {
  from: Date | null;
  to: Date | null;
}

export type FormDatePickerV2Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string;
  placeholder?: string;
  description?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  datePickerClassName?: string;
  descriptionClassName?: string;
  messageClassName?: string;
  disabled?: boolean;
  showTime?: boolean;
  range?: boolean;
  monthOnly?: boolean;
  dateFormat?: string;
  onValueChange?: (value: Date | DateRange | null) => void;
  preserveMessageSpace?: boolean;
  required?: boolean;
  separator?: boolean;
};

export const FormDatePickerV2 = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  label,
  name,
  description,
  wrapperClassName,
  labelClassName,
  datePickerClassName,
  descriptionClassName,
  messageClassName,
  placeholder = "Select date",
  disabled = false,
  showTime = false,
  range = false,
  monthOnly = false,
  dateFormat,
  onValueChange,
  preserveMessageSpace = false,
  required,
  separator = false,
  ...props
}: FormDatePickerV2Props<TFieldValues, TName>) => {
  const formatDateValue = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "string" && dayjs(value).isValid()) {
      return dayjs(value).toDate();
    }
    return null;
  };

  const formatRangeValue = (value: any): DateRange | null => {
    if (!value) return null;
    if (typeof value === "object" && "from" in value && "to" in value) {
      return {
        from: formatDateValue(value.from),
        to: formatDateValue(value.to),
      };
    }
    return null;
  };

  const formatOutputValue = (
    date: Date | DateRange | null,
  ): string | { from: string | null; to: string | null } | null => {
    if (!date) return null;

    const defaultFormat = showTime ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD";

    if (range && typeof date === "object" && "from" in date) {
      const rangeDate = date as DateRange;
      return {
        from: rangeDate.from
          ? dayjs(rangeDate.from).format(dateFormat ?? defaultFormat)
          : null,
        to: rangeDate.to
          ? dayjs(rangeDate.to).format(dateFormat ?? defaultFormat)
          : null,
      };
    }

    if (date instanceof Date) {
      return dayjs(date).format(dateFormat ?? defaultFormat);
    }

    return null;
  };

  return (
    <FormField
      control={control}
      name={name}
      {...props}
      render={({ field, fieldState }) => {
        const currentValue = range
          ? formatRangeValue(field.value)
          : formatDateValue(field.value);

        return (
          <FormItem
            className={cn(
              "aero:flex aero:flex-col aero:w-full aero:gap-2",
              wrapperClassName,
            )}
            aria-invalid={!!fieldState?.error}
          >
            {label && (
              <FormLabel
                className={cn(
                  required &&
                    "aero:flex aero:items-center aero:justify-between",
                  labelClassName,
                )}
              >
                {label}{" "}
                {required && (
                  <span className="aero:text-destructive" aria-hidden="true">
                    *
                  </span>
                )}
              </FormLabel>
            )}
            <FormControl>
              <>
                {range ? (
                  <DatePickerV2
                    range={true}
                    rangeValue={currentValue as DateRange | null}
                    onRangeChange={(newRange) => {
                      const formattedValue =
                        formatOutputValue(newRange) ?? null;
                      field.onChange(formattedValue);
                      if (onValueChange) {
                        onValueChange(newRange);
                      }
                    }}
                    onBlur={field.onBlur}
                    disabled={disabled}
                    placeholder={placeholder}
                    className={datePickerClassName}
                    showTime={showTime}
                    hasError={!!fieldState?.error}
                  />
                ) : (
                  <DatePickerV2
                    value={currentValue as Date | null}
                    onChange={(newDate) => {
                      const formattedValue = formatOutputValue(newDate) ?? "";
                      field.onChange(formattedValue);
                      if (onValueChange) {
                        onValueChange(newDate);
                      }
                    }}
                    onBlur={field.onBlur}
                    disabled={disabled}
                    placeholder={placeholder}
                    className={datePickerClassName}
                    showTime={showTime}
                    monthOnly={monthOnly}
                    hasError={!!fieldState?.error}
                  />
                )}
              </>
            </FormControl>
            {description && (
              <FormDescription className={descriptionClassName}>
                {description}
              </FormDescription>
            )}
            {separator && <Separator className="aero:my-1" />}
            <div
              className={cn(preserveMessageSpace ? "aero:min-h-[18px]" : "")}
            >
              <FormMessage
                className={cn(
                  fieldState?.error
                    ? "aero:text-destructive aero:text-xs"
                    : preserveMessageSpace
                      ? "aero:invisible aero:text-xs"
                      : "aero:hidden",
                  messageClassName,
                )}
              />
            </div>
          </FormItem>
        );
      }}
    />
  );
};
